import { createSlice, PayloadAction, AnyAction } from "@reduxjs/toolkit";
// Types
import Reducers from "app/types/Reducers";
// Models
import IContact from "app/models/Contact";
// Async
import { getContact, getContacts, createContact, updateContact, deleteContact, getContactsAutocomplete } from './Contacts.async';
import ContactTypes from "app/types/ContactTypes";

interface IState {
  contact: IContact | null;
  contacts: IContact[] | null;
  autocomplete: IContact[] | null;
  total: number;
  loading: boolean;
  autocompleteLoading: boolean;
  params: {
    limit: number;
    offset: number;
    search: string;
    type: ContactTypes | 'all';
    referenceContactId: number | null;
  };
  filters: {
    referenceContact: IContact | null;
  }
}

const initialState:IState = {
  contact: null,
  contacts: null,
  autocomplete: null,
  total: 0,
  loading: false,
  autocompleteLoading: false,
  params: {
    limit: 20,
    offset: 0,
    search: '',
    type: 'all',
    referenceContactId: null
  },
  filters: {
    referenceContact: null
  }
};

const slice = createSlice({
  name: Reducers.Contacts,
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<IState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    // Default
    setInitialField: <IStateKey extends keyof IState>(state: IState, action: PayloadAction<IStateKey>) => {
      state[action.payload] = initialState[action.payload];
    },
    resetState: () => initialState
  },
  extraReducers: (builder) => {
    // Get contact
    builder.addCase(getContact.pending, (state) => {
      state.contact = null;
    });
    builder.addCase(getContact.fulfilled, (state, action:PayloadAction<IContact>) => {
      state.contact = action.payload;
    });
    // Get contacts
    builder.addCase(getContacts.pending, (state, action:any) => {
      state.params = action.meta.arg;
    });
    builder.addCase(getContacts.fulfilled, (state, action:PayloadAction<{ data:IContact[], total:number }>) => {
      state.contacts = action.payload.data;
      state.total = action.payload.total;
    });
    // Get contacts autocomplete
    builder.addCase(getContactsAutocomplete.pending, (state, action:any) => {
      state.autocomplete = null;
      state.autocompleteLoading = true;
    });
    builder.addCase(getContactsAutocomplete.fulfilled, (state, action:PayloadAction<{ data:IContact[], total:number }>) => {
      state.autocomplete = action.payload.data;
      state.autocompleteLoading = false;
    });
    builder.addCase(getContactsAutocomplete.rejected, (state) => {
      state.autocompleteLoading = false;
    });
    // Create contact
    builder.addCase(createContact.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createContact.fulfilled, (state, action:PayloadAction<IContact>) => {
      if ( state.contacts ){
        state.contacts.push(action.payload);
        state.total += 1;
      }
    });
    // Update contact
    builder.addCase(updateContact.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateContact.fulfilled, (state, action:PayloadAction<IContact>) => {
      if ( state.contacts ){
        state.contacts = state.contacts.map((contact:IContact) => {
          if ( contact.id === action.payload.id ) return action.payload;
          return contact;
        })
      }
    });
    // Delete contact
    builder.addCase(deleteContact.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deleteContact.fulfilled, (state, action:{ payload:number }) => {
      if ( state.contacts ){
        state.contacts = state.contacts.filter((contact:IContact) => contact.id !== action.payload);
        state.total -= 1;
      }
    });
    // Match
    builder.addMatcher(
      (action:AnyAction) => action.type.includes('fulfilled') || action.type.includes('rejected'),
      (state) => {
        state.loading = false;
      }
    )
  }
});

export const ContactsActions = slice.actions;

export default slice.reducer;